import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Logo from "../../Assets/Images/logo.png";
import PasswordChangedSuccess from "../../Assets/Images/password-changed.svg";
import hideicon from "../../Assets/Images/hideicone.svg";
import showicon from "../../Assets/Images/showIcon.svg";
import sucess from "../../Assets/Images/successIcon.svg";
import { CircularProgress } from "@mui/material";
import TPLogo from '../../Assets/Images/TPLogo.png';
import NewTPLogo from '../../Assets/Images/whiteLogo.png';

const isLinkError = (res) => ["link_expired", "link_invalid"].includes(res?.reason) || /link expired/i.test(res?.message || "");

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get("email");
  let verifyString = params.get("code");
  const reset = params.get("reset");

  const [passwordChanged, setPasswordChanged] = useState(reset || false);
  const [hideNewPassword, setHideNewPassword] = useState(true);
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true);
  const [error, setError] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [linkStatus, setLinkStatus] = useState(reset ? "valid" : "checking");
  const [linkExpired, setLinkExpired] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [resend, setResend] = useState({ loading: false, sent: false, error: "" });

  useEffect(() => {
    if (reset) return;
    if (!email || !verifyString) {
      setLinkStatus("expired");
      return;
    }
    const checkLink = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_URI + "/v4/organisation/user/verify/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email, verifyString: verifyString }),
        });
        const res = await response.json();
        setLinkStatus(isLinkError(res) ? "expired" : "valid");
      } catch (err) {
        setLinkStatus("valid");
      }
    };
    checkLink();
  }, [email, verifyString, reset]);

  const sendNewLink = async () => {
    if (!email) {
      navigate("/forget-password");
      return;
    }
    setResend({ loading: true, sent: false, error: "" });
    try {
      const response = await fetch(process.env.REACT_APP_API_URI + "/v4/organisation/forgot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          assessmentId: process.env.REACT_APP_CLIENT_ID,
        }),
      });
      const res = await response.json();
      if (res.status === "success") {
        setResend({ loading: false, sent: true, error: "" });
      } else {
        setResend({ loading: false, sent: false, error: res?.message || "An error occurred. Please try again later." });
      }
    } catch (err) {
      setResend({ loading: false, sent: false, error: "An error occurred. Please try again later." });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();

  let tempErrors = { newPassword: "", confirmPassword: "" };
  let hasError = false;

  if (!data.newPassword) {
    tempErrors.newPassword = "Please enter a new password.";
    hasError = true;
  } else if (data.newPassword.length < 8) {
    tempErrors.newPassword = "Password must be at least 8 characters.";
    hasError = true;
  }

  if (!data.confirmPassword) {
    tempErrors.confirmPassword = "Please confirm the new password.";
    hasError = true;
  } else if (data.newPassword !== data.confirmPassword) {
    tempErrors.confirmPassword = "Passwords must match. Please try again.";
  hasError = true;
  }

  if (hasError) {
    setError(tempErrors);
    return;
  }

    setError({ newPassword: "", confirmPassword: "" });
    setSubmitError("");
    setLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_API_URI + "/v4/organisation/user/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          password: data.newPassword,
          verifyString: verifyString,
        }),
      });

      const res = await response.json();
      if (res.status === "success") {
        setPasswordChanged(true);
        navigate('/account/verify?reset=true')
      } else if (isLinkError(res)) {
        setLinkExpired(true);
      } else {
        setSubmitError(res?.message || "An error occurred. Please try again.");
      }
    } catch (err) {
      setSubmitError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleNewPasswordVisibility = () => {
    setHideNewPassword(!hideNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setHideConfirmPassword(!hideConfirmPassword);
  };

  return (
    <div className="container change-password-container">
      <div className="inner-container">
      <div className="left">
        <img src={NewTPLogo} alt="Logo" style={{ width: "113", height: "129" , objectFit: "contain"}} />
        <h1>Welcome to the <br /> Quadrant Flow Portal  </h1>
      </div>
        <div className="right">
          {linkStatus === "checking" ? (
            <CircularProgress size={40} style={{ color: "#BF9540" }} />
          ) : linkStatus === "expired" ? (
            <div className="link-expired">
              <h2>This link has expired</h2>
              <p className="link-expired-text">For your security, password reset links expire after 30&nbsp;minutes. Please request a new link to reset your&nbsp;password.</p>
              {resend.sent ? (
                <p className="link-sent"><img src={sucess} alt="" />A new reset link has been sent to {email}.</p>
              ) : (
                <button type="button" onClick={sendNewLink} disabled={resend.loading}>
                  {resend.loading ? <CircularProgress size={25} style={{ color: "#fff" }} /> : "Send a new link"}
                </button>
              )}
              {resend.error && <p className="submit-error">{resend.error}</p>}
              <Link to={"/login"} className="back-to-login">Back to login</Link>
            </div>
          ) : !passwordChanged ? (
            <form className="form" onSubmit={changePassword}>
              <h2>Change Password</h2>
              <div className="input-box">
                <label htmlFor="newPassword">New Password<span className="asterisk-mark">*</span></label>
                <div className="input-wrapper">
                  <input type={hideNewPassword ? "password" : "text"} name="newPassword" id="newPassword" value={data.newPassword} onChange={handleChange} onKeyDown={(e) => e.key === " " && e.preventDefault()} placeholder="Type a new password" />
                  <img src={hideNewPassword ? hideicon : showicon} alt={hideNewPassword ? "Show password" : "Hide password"} onClick={toggleNewPasswordVisibility} className="toggle-icon" role="button" aria-label="Toggle password visibility" />
                </div>
              </div>
              {error.newPassword && <p className="error-text">{error.newPassword}</p>}
              <div className="input-box">
                <label htmlFor="confirmPassword">Confirm New Password<span className="asterisk-mark">*</span></label>
                <div className="input-wrapper">
                  <input type={hideConfirmPassword ? "password" : "text"} name="confirmPassword" id="confirmPassword" value={data.confirmPassword} onChange={handleChange} onKeyDown={(e) => e.key === " " && e.preventDefault()} placeholder="Confirm new Password" />
                  <img src={hideConfirmPassword ? hideicon : showicon} alt={hideConfirmPassword ? "Show password" : "Hide password"} onClick={toggleConfirmPasswordVisibility} className="toggle-icon" role="button" aria-label="Toggle password visibility" />
                </div>
              </div>
              {error.confirmPassword && <p className="error-text">{error.confirmPassword}</p>}
              {linkExpired && !resend.sent && (
                <p className="submit-error">
                  This reset link has expired. Please request a new one.{" "}
                  <button type="button" className="text-link" onClick={sendNewLink} disabled={resend.loading}>
                    {resend.loading ? "Sending..." : "Send a new link"}
                  </button>
                </p>
              )}
              {linkExpired && resend.sent && <p className="link-sent"><img src={sucess} alt="" />A new reset link has been sent to {email}.</p>}
              {linkExpired && resend.error && <p className="submit-error">{resend.error}</p>}
              {submitError && <p className="submit-error">{submitError}</p>}
              <button type="submit" disabled={loading}>
                {loading ? <CircularProgress size={25} style={{ color: "#fff" }} /> : "Reset Your Password"}
              </button>
            </form>
          ) : (
            <div className="password-changed">
              <img src={PasswordChangedSuccess} alt="" />
              <h2>Password Changed!</h2>
              <p className="password-success">Your password has been changed successfully!</p>
              <Link to={"/login"}>
                <button>Log In</button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
